<!--
	@component
	core wrapper around svelecte 5.
	FIXME: refactor!!!!!
	TODO: move all types into autocompleteFieldConfig.ts or a dedicated .model.ts

	HINT: consumers opt into `valueAsObject: true` via `autocompleteConfig` —
	when set, `value` holds the full selected object (not just an ID).
	`selectedObject` is @deprecated — use `bind:value` instead.

	@param {object | null} value - bindable. selected value — ID string or full object depending on `valueAsObject`.
	@param {object[]} options - option list passed to svelecte. NOT bindable in svelecte v5.
	@param {boolean} disabled - bindable. controls disabled state.
	@param {SvelecteControlApi | null} svelecte - bindable. exposes `refetchWith()`, `focus()`, `getSelection()`.
	@param {AutocompleteFieldConfig} autocompleteConfig - spread into svelecte. `strictMode` defaults to `false`.
	@param {string} label - field label (non-material-design mode).
	@param {(value: object) => void} onChange - callback receiving the selected value/object directly.
	@param {Snippet} controlEnd - snippet rendered after the input (e.g. add-person button).
	@param {Snippet} selection - custom selection rendering, overrides default.
	@param {Snippet} option - custom option rendering, overrides default.

	@see AddressDataAutocomplete for address form usage.
	@see PersonAndCompanyDataAutocomplete for person/company usage.
	@see docs/SVELECTE-GRAPH.md for the full consumer graph.
-->

<script lang="ts">
	import Svelecte, { config, highlightSearch } from 'svelecte'
	import type { Snippet } from 'svelte'
	import Tooltip from '../atoms/Tooltip.svelte'
	import { t } from '$i18n/i18n'

	import type { FontProps } from '$types/fontProps'
	import {
		DefaultInputFieldProps,
		DefaultRobotoFontProps,
		getFontPropsStyleString,
		getFontPropsVarString
	} from '$types/fontProps'
	import type {
		AutocompleteFieldConfig,
		SearchProps,
		SvelecteControlApi,
		SvelecteFieldComponentProps
	} from '$types/autocompleteFieldConfig'
	import { DefaultAutocompleteConfig } from '$types/autocompleteFieldConfig'
	import { getContext, hasContext, onMount } from 'svelte'
	import { markFieldLabelRequired } from '$utils/formatters'
	import { onClassChange } from '$utils/mutationObserver'
	import { openCloseVariants } from '$utils/svelteMotionUtils'
	import { AnimatePresence, AnimateSharedLayout, Motion } from 'svelte-motion'
	import {
		getExactPathErrorMessage,
		getPathErrorMessage,
		isExactPathInErrorList,
		isPathInErrorList
	} from '$utils/formValidation'
	import type { Writable } from 'svelte/store'
	import type { FormError } from '$types/formError'
	import { flip } from 'svelte/animate'

	type SvelecteField = SvelecteFieldComponentProps
	type ItemLabelFn = (item: object, selectionSection?: boolean, inputValue?: string) => string

	// TODO: why is it using store syntax  `$` ?
	type SvelecteOptionRow = object & { $selected?: boolean }

	/* HINT: svelecte types `selection` snippet’s `bindItem` as `Function`; keep aligned for assignability. */
	// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- matches svelecte `Svelecte.svelte.d.ts`
	type SvelecteBindItem = Function

	config.i18n = {
		...config.i18n,
		empty: $t('UI.map.svelecte.empty'),
		nomatch: $t('UI.map.svelecte.nomatch'),
		max: (num) => $t('UI.map.svelecte.max', { num: num }),
		fetchBefore: $t('UI.map.svelecte.fetchBefore'),
		fetchQuery: (minQuery) =>
			`Tippe ${minQuery > 1 ? `mindestens ${minQuery} Zeichen ` : ''}, um zu suchen`,
		fetchEmpty: $t('UI.map.svelecte.fetchEmpty'),
		collapsedSelection: (count) => $t('UI.map.svelecte.collapsedSelection', { count: count }),
		createRowLabel: (value) => $t('UI.map.svelecte.createRowLabel', { value: value }),
		emptyCreatable: $t('UI.svelecte.typeToCreate')
	}

	const useMaterialDesign: boolean = hasContext('useMaterialDesign')
		? getContext('useMaterialDesign')
		: false

	// TODO: document what all of them mean
	interface Props {
		materialWidth?: string
		/** With valueAsObject: true, value holds the full selected object (or null), not just an ID. */
		value?: SvelecteField['value']
		/** @deprecated use bind:value instead — kept for backwards compatibility during migration */
		selectedObject?: SvelecteField['value']
		label?: string
		placeholder?: string | null
		ttText?: string
		helperText?: string
		borderRadius?: string
		fontProps?: FontProps
		headerFontProps?: FontProps
		autocompleteConfig?: AutocompleteFieldConfig
		options?: SvelecteField['options']
		hideDropdownArrow?: boolean
		required?: boolean
		renderer?: SvelecteField['renderer'] | null
		containerWidth?: string
		disabled?: boolean
		disableCreateRowLabel?: boolean
		height?: string
		id?: string
		dropdownMaxHeight?: string
		positionResolver?: SvelecteField['positionResolver']
		svelecte?: SvelecteControlApi | null
		errorPath?: string
		onChange?: (selection: SvelecteField['value']) => void
		onBlur?: SvelecteField['onBlur']
		onFocus?: SvelecteField['onFocus']
		onFetch?: SvelecteField['onFetch']
		onClear?: () => void
		icon?: Snippet
		selection?: Snippet<[object[], SvelecteBindItem]>
		option?: Snippet<[SvelecteOptionRow, string, typeof highlightSearch, ItemLabelFn]>
		controlEnd?: Snippet
	}

	let {
		materialWidth = '100%', // TODO: why? And why not auto?
		value = $bindable(), // TODO: need to migrate all consumers to this prop
		// eslint-disable-next-line no-useless-assignment -- @deprecated - TODO: get rid of it
		selectedObject = $bindable(),
		label = '',
		placeholder = '',
		ttText = '',
		helperText = '',
		borderRadius = '2px',
		fontProps = DefaultInputFieldProps(),
		headerFontProps = DefaultRobotoFontProps('14px', '0.875rem'),
		autocompleteConfig = DefaultAutocompleteConfig(),
		options = $bindable([]),
		hideDropdownArrow = true,
		required = false,
		renderer = null,
		containerWidth = '100%',
		disabled = $bindable(autocompleteConfig.disabled),
		disableCreateRowLabel = false,
		height = '2rem',
		id = 'autocomplete-id' + Math.random().toString(16).slice(2), // TODO: use uuid from Svelte or nanoid
		dropdownMaxHeight = 'unset',
		positionResolver = undefined,
		svelecte = $bindable(null),
		errorPath = '',
		onChange,
		onBlur,
		onFocus,
		onFetch,
		onClear,
		icon,
		selection,
		option,
		controlEnd
	}: Props = $props()

	const inputId = $derived(id + '-input')

	// TODO: simplify - notEmpty and isValueSelected are probably two states of the same condition
	let isValueSelected = $derived.by(() => checkNotEmpty())
	let notEmpty: boolean | undefined = $derived(!!(isValueSelected || (placeholder && label)))

	let isFocused = $state(false)
	let closestChangingField: Element | null = null

	// TODO: should be a css class / prop
	const activeBorder = useMaterialDesign
		? '2px solid var(--sv-border-color-active)'
		: '1px solid var(--sv-border-color-active)'

	// this belongs in .config.ts (or .model.ts)
	const defaultSearchProps: Partial<SearchProps> = {
		skipSort: true,
		disabled: false,
		startOnly: false,
		wordsOnly: false
	}

	/* Handling of the error message indication with a context store that is set on the parent including the form */
	// TODO: why here? And why not in the parent?
	let formErrors: Writable<FormError[]> | null = hasContext('formErrors')
		? getContext('formErrors')
		: null

	// TODO: what type of error? From what behaviour?
	let error = $state('')

	// TODO: ??? Sounds like this belongs into a util
	$effect(() => {
		if (errorPath && errorPath !== '' && $formErrors != null) {
			let paths = errorPath.includes('||') ? errorPath.split('||') : [errorPath]
			let newError = ''
			paths.forEach((path) => {
				if (path.includes('.*')) {
					newError += isPathInErrorList($formErrors, path)
						? getPathErrorMessage($formErrors, path)
						: ''
				} else {
					newError += isExactPathInErrorList($formErrors, path)
						? getExactPathErrorMessage($formErrors, path)
						: ''
				}
			})
			error = newError
		}
	})

	// TODO: sounds like a util; purpose unclear
	function itemRenderer(item: object | null | undefined): string {
		if (item == null) return ''
		if (renderer != null && typeof renderer === 'function') {
			return String(renderer(item, false, undefined) ?? '')
		}
		const labelField = autocompleteConfig.labelField
		if (labelField != null && labelField !== '') {
			const labelFieldValue = (item as Record<string, unknown>)[labelField]
			return labelFieldValue != null ? String(labelFieldValue) : ''
		}
		const row = item as Record<string, unknown>
		const label = row['label']
		const text = row['text']
		if (label != null) return String(label)
		if (text != null) return String(text)
		return ''
	}

	const itemLabelForHighlight: ItemLabelFn = (item) => itemRenderer(item)

	function checkNotEmpty(): boolean {
		// if multiselect, check if array is not 0, else check value for truthiness
		return Array.isArray(value) ? value.length > 0 : !!value
	}

	onMount(() => {
		if (required && label !== '') {
			label = markFieldLabelRequired(label)
		}
		const inputField = document.getElementById(inputId)

		// FIXME: confirm this still works in Svelte 5 / after the Svelte 5 migration
		// not very Svelte-ish
		if (inputField) {
			closestChangingField = inputField.closest('.svelecte-control')

			if (closestChangingField) {
				// HINT: this is a mutation observer
				onClassChange(closestChangingField, (node: Element) => {
					isFocused = node.classList.contains('is-focused')
				})
			}
		}
	})

	const hideSeparator = $derived(controlEnd === undefined)
	const fontPropsStyleString = $derived(getFontPropsStyleString(fontProps))
	const fontPropsVarString = $derived(getFontPropsVarString(fontProps))

	$effect(() => {
		// FIXME: effect for state; might cause race condition or infinite loop
		// probably belongs in onMount or .config.ts
		if (disableCreateRowLabel) {
			config.i18n = {
				...config.i18n,
				empty: $t('UI.map.svelecte.empty'),
				nomatch: $t('UI.map.svelecte.nomatch'),
				max: (num) => $t('UI.map.svelecte.max', { num: num }),
				fetchBefore: $t('UI.map.svelecte.fetchBefore'),
				fetchQuery: (minQuery) =>
					`Tippe ${minQuery > 1 ? `mindestens ${minQuery} Zeichen ` : ''}, um zu suchen`,
				fetchEmpty: $t('UI.map.svelecte.fetchEmpty'),
				collapsedSelection: (count) => $t('UI.map.svelecte.collapsedSelection', { count: count }),
				createRowLabel: (value) => `${value}`
			}
		}
	})

	function handleChange(selectionValue: SvelecteField['value']) {
		// sync deprecated selectedObject for backwards compat
		// TODO: de-duplicate / deprecate the deprecated prop
		selectedObject = value
		onChange?.(selectionValue)
	}

	/**
	 * Fallback selection check for the dropdown row's checkmark.
	 * With `valueAsObject: true` and `strictMode: false` (our wrapper default), svelecte's
	 * `watch_value_change` deselects the option living in `options_flat` and marks a fresh
	 * `Object.fromEntries(...)` copy as `$selected` instead — so the row in the dropdown
	 * always ends up with `$selected === false`. Mirror svelecte's own virtual-list
	 * fallback (`opt.$selected || selectedKeys.has(...)`) by comparing item to `value`
	 * via the configured value field.
	 */
	function isItemSelected(item: SvelecteOptionRow): boolean {
		if (item.$selected) return true
		const vf = autocompleteConfig.valueField ?? 'value'
		const itemKey = (item as Record<string, unknown>)[vf]
		if (itemKey == null) return false

		const keyOf = (v: unknown): unknown =>
			autocompleteConfig.valueAsObject ? (v as Record<string, unknown> | null)?.[vf] : v

		if (autocompleteConfig.multiple) {
			return Array.isArray(value) && (value as unknown[]).some((v) => keyOf(v) === itemKey)
		}
		return value != null && keyOf(value) === itemKey
	}
</script>

<!-- Default snippets for Svelecte v5 -->
<!-- TODO: what is the default selction for? -->
{#snippet defaultSelection(
	// TODO: confirm type or narrow down to SvelecteOptionRow[]
	selectedOptions: object[] | SvelecteOptionRow[],
	bindItem: SvelecteBindItem
)}
	{#each selectedOptions as opt ((opt as Record<string, unknown>)[autocompleteConfig.valueField ?? 'value'])}
		<div
			class="sv-item--container"
			animate:flip={{ duration: 0 }}
			onmousedown={(e) => e.preventDefault()}
			role="option"
			tabindex={0}
			aria-selected={(opt as SvelecteOptionRow)?.$selected}
		>
			<div class="sv-item--wrap" class:is-multi={autocompleteConfig?.multiple}>
				<div class="sv-item--content">{@html itemRenderer(opt)}</div>
			</div>
			{#if autocompleteConfig?.multiple}
				<button
					class="sv-item--btn"
					type="button"
					aria-label="Deselect"
					data-action="deselect"
					use:bindItem={opt}
				>
					<!-- TODO: turn into Svg component -->
					<svg height="16" width="16" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
						<path
							d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"
						></path>
					</svg>
				</button>
			{/if}
		</div>
	{/each}
{/snippet}

<!-- this is the default renderer of our options list items, so we show a checkmark and the text with the highlighted search -->
{#snippet defaultOption(item: SvelecteOptionRow, inputValue: string)}
	<div class="multiselect-options">
		<div
			class="icon-checkbox"
			class:selected={isItemSelected(item)}
			class:borderless={!autocompleteConfig.multiple}
		></div>
		<div class="sv-item--content" title={itemRenderer(item)}>
			<span>{@html highlightSearch(item, true, inputValue, itemLabelForHighlight, false)}</span>
		</div>
	</div>
{/snippet}

{#snippet clearIconSnippet(selectedOptions: object[])}
	<!-- TODO: make button; use new class btn-icon-only -->
	<div class="close-icon" onclick={() => onClear?.()}>
		{#if selectedOptions.length}
			<div id="{id}-clearIcon" class="clearable-icon">
				<i class="bx bx-x"></i>
			</div>
		{/if}
	</div>
{/snippet}

{#snippet toggleIconSnippet(isOpen: boolean)}
	{#if !hideDropdownArrow && !isValueSelected}
		<i class="bx bx-chevron-down sv-toggle" class:bx-rotate-180={isOpen}></i>
	{/if}
{/snippet}

<!-- Bridge snippets: delegate to consumer snippet (passed from parent) or default -->
{#snippet selectionBridge(selectedOptions: object[], bindItem: SvelecteBindItem)}
	{#if selection}
		{@render selection(selectedOptions, bindItem)}
	{:else}
		{@render defaultSelection(selectedOptions, bindItem)}
	{/if}
{/snippet}

<!-- same like selectionBridge, but for option -->
{#snippet optionBridge(item: SvelecteOptionRow, inputValue: string)}
	{#if option}
		{@render option(item, inputValue, highlightSearch, itemLabelForHighlight)}
	{:else}
		{@render defaultOption(item, inputValue)}
	{/if}
{/snippet}

<AnimateSharedLayout>
	<div {id} class="input-field-error-message-container" style="--container-width: {containerWidth}">
		<div
			class="autocomplete-container"
			class:hideDropdownArrow
			class:isValueSelected
			class:hideSeparator
			class:isFocused
			class:material-design={useMaterialDesign}
			class:noClear={!autocompleteConfig.clearable}
			style="width: 100%;
                --input-width: {materialWidth};
                --dropdown-max-height: {dropdownMaxHeight};
                --sv-border-radius:{borderRadius};
                --sv-border: 1px solid var(--sv-border-color);
                --sv-active-border: {activeBorder};
                --sv-border-color: #D9D9D9 !important;
                --sv-border-color-active: #58C079 !important;
                --sv-highlight-bg: rgba(88, 192, 121, 0.3) !important;
                --sv-placeholder-color: {fontProps.placeholderColor || '#9d9d9d'};
                --sv-bg: white;
                --sv-item-active-bg: #F0F2F8 !important;
                --sv-min-height: {height};
                {fontPropsStyleString || ''}
                {fontPropsVarString || ''}"
		>
			{#if label && label !== '' && !useMaterialDesign}
				<div class="label-container">
					<span class="input-label-variable" style={getFontPropsVarString(headerFontProps)}
						>{label}</span
					>
					{#if ttText !== undefined && ttText !== ''}
						<div>
							<Tooltip msg={ttText} />
						</div>
					{/if}
				</div>
			{/if}
			<!--
				HINT: Critical fix found during browser verification: 
				Svelecte v5 defaults `strictMode: true` and clears values that don't match loaded options.
				This broke catalog/material fields where the value (ID) is set before options load.
				Fixed by defaulting `strictMode` to `false` in the wrapper (matching v4's lenient behavior),
				while still respecting explicit `strictMode` settings from consumers.
			-->
			<Svelecte
				bind:this={svelecte}
				bind:value
				{...autocompleteConfig}
				strictMode={autocompleteConfig.strictMode ?? false}
				minQuery={1}
				{options}
				{inputId}
				renderer={renderer ?? undefined}
				{disabled}
				placeholder={placeholder ?? undefined}
				{positionResolver}
				searchProps={{ ...defaultSearchProps, ...autocompleteConfig.searchProps }}
				collapseSelection={autocompleteConfig.multiple ? 'always' : undefined}
				keepSelectionInList
				highlightFirstItem={false}
				onChange={handleChange}
				{onBlur}
				{onFocus}
				{onFetch}
				selection={selectionBridge}
				option={optionBridge}
				clearIcon={clearIconSnippet}
				toggleIcon={toggleIconSnippet}
				prepend={icon}
				append={controlEnd}
			/>
			{#if useMaterialDesign}
				<label
					class="material-label"
					style={fontPropsVarString}
					for={inputId}
					id="label-{inputId}"
					class:notEmpty
					class:isFocused
					class:material-label-disabled={disabled}
				>
					<div class="material-text">{label}</div>
				</label>
			{/if}
		</div>
		{#if helperText && helperText !== '' && (!error || error === '')}
			<div class="input-helper-text">{helperText}</div>
		{/if}
		<AnimatePresence list={error && error !== '' ? [{ key: 1 }] : []}>
			<Motion
				initial="collapsed"
				animate="open"
				exit="collapsed"
				variants={openCloseVariants}
				let:motion={collapse}
			>
				<span class="form-field-error" use:collapse>{@html error}</span>
			</Motion>
		</AnimatePresence>
	</div>
</AnimateSharedLayout>

<style lang="scss">
	@import '$styles/global';
	@import '$styles/inputFields';
	@import '$styles/autocomplete';

	.label-container {
		@include flex-row(0.25rem, $alignment: flex-end, $justify: flex-start);

		position: relative;
	}

	.autocomplete-container {
		width: 100%;

		@include flex-col(0.375rem, $justify: flex-start, $alignment: flex-start);

		& :global(.svelecte) {
			width: 100%;
		}

		&:hover {
			cursor: text !important;
		}
	}

	.autocomplete-container :global(.sv-control) {
		border-radius: var(--sv-border-radius) !important;
		height: var(--sv-height) !important;
		min-height: 2rem !important;
	}

	.autocomplete-container :global(.sv-content) {
		padding: 0.25rem 0.25rem 0 0.5rem;
	}

	.autocomplete-container :global(.sv-control.is-disabled),
	.autocomplete-container :global(.sv-control.is-disabled > .sv-content),
	.autocomplete-container :global(.sv-control.is-disabled > .sv-content > div),
	.autocomplete-container :global(.sv-control.is-disabled > .sv-content > div > .sv-item),
	.autocomplete-container :global(.sv-control.is-disabled > .sv-content .sv-item--content) {
		cursor: not-allowed;
		pointer-events: auto;
		color: $grey-800 !important;
	}

	// while typing a new search over a single-select, hide the still-selected
	// value so it doesn't ghost behind the input text.
	.autocomplete-container :global(.sv-control--selection.is-single.has-input .sv-item--container) {
		// svelecte fades to 0.2
		opacity: 0 !important;
	}

	// remove the default input field left padding
	.autocomplete-container
		:global(.sv-control--selection.is-single .sv-item--container + .sv-input--sizer) {
		padding-left: 0 !important;
	}

	.hideSeparator :global(.indicator-separator) {
		// hides the separator line
		display: none;
	}

	.autocomplete-container :global(div .indicator-container.close-icon) {
		padding: 0 8px;
	}

	.autocomplete-container:not(.noClear) :global(.indicator-container:first-of-type > svg) {
		// when clearable is not activated, the dropdownArrow is hidden
		display: none;
	}

	.hideDropdownArrow,
	.isValueSelected {
		:global(button.sv-btn-indicator[data-action='toggle']) {
			// hides the dropdown arrow
			display: none;
		}
	}

	/* always hide the separator */
	:global(span.sv-btn-separator) {
		display: none;
	}

	// explicitly leave this here as information
	.autocomplete-container :global(.sv-item--content) {
		display: initial; // we must not use flex here, because this destroys the highlighting and removes spaces between words
	}

	.autocomplete-container :global(.sv-dd-item > .sv-item) {
		// makes padding only to the items in the dropdown list, not to the selected ones
		padding-left: 1rem !important; // eventually put this one in RoutePlanerSearchBox as well, if not applicable to all usages of AddressSearchbox
		line-height: 1.5 !important; // otherwise, the options look too small
	}

	.autocomplete-container :global(.sv-optgroup-header) {
		text-align: start;
		padding: 0.5rem 0.75rem;
		background: var(--color-gray-50);
	}

	:global(.sv_dropdown) {
		max-height: var(--dropdown-max-height);
	}

	:global(.sv-control--selection) {
		--sv-selection-gap: 4px 12px;
	}
</style>
